import { useEffect, useState } from "react";
import { formatUnits } from "ethers";
import { useCatDetails } from "@/hooks/useCatDetails";
import { useContracts } from "@/hooks/useContracts";
import { ASSETS } from "@/lib/contracts";
import { OpenSeaLink } from "@/components/shared/OpenSeaLink";
import type { Rarity } from "@/types/cat";
import { RarityBadge } from "@/components/shared/RarityBadge";
import { Skeleton } from "@/components/ui/skeleton";

interface CatPreviewCardProps {
  tokenId?: number | null;
}

function rarityFromWeight(weight: number): Rarity {
  if (weight >= 160) return "mythic";
  if (weight >= 135) return "legendary";
  if (weight >= 115) return "rare";
  return "common";
}

export function CatPreviewCard({ tokenId }: CatPreviewCardProps) {
  const { details, isLoading } = useCatDetails(tokenId);
  const { vault } = useContracts();
  const [balances, setBalances] = useState<bigint[]>([0n, 0n, 0n]);

  useEffect(() => {
    if (!tokenId) {
      setBalances([0n, 0n, 0n]);
      return;
    }
    let cancelled = false;
    vault
      .balancesOf(tokenId)
      .then((result: [string[], bigint[]]) => {
        if (!cancelled) setBalances(result[1]);
      })
      .catch(() => {
        if (!cancelled) setBalances([0n, 0n, 0n]);
      });
    return () => {
      cancelled = true;
    };
  }, [tokenId, vault]);

  if (!tokenId) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-center text-muted-foreground">
        Select a cat above to preview details.
      </div>
    );
  }

  if (isLoading || !details) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6">
        <Skeleton className="aspect-square w-full rounded-xl" />
        <Skeleton className="mt-4 h-6 w-1/2" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="relative aspect-square overflow-hidden rounded-xl border border-border">
        <img
          src={`/images/nfts-thumb/${tokenId}.webp`}
          alt={`Cat #${tokenId}`}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>

      <div className="mt-4 flex items-start justify-between gap-3">
        <h3 className="font-pixel text-lg">Cat #{tokenId}</h3>
        <OpenSeaLink tokenId={tokenId} className="shrink-0" />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <RarityBadge rarity={rarityFromWeight(Number(details.rarityWeight))} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl border border-border bg-background p-3">
          <p className="text-xs text-muted-foreground">Status</p>
          <p className={details.active ? "text-success" : "text-muted-foreground"}>
            {details.active ? "Active" : "Dormant"}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-background p-3">
          <p className="text-xs text-muted-foreground">Tier</p>
          <p className="font-mono">{details.tier}</p>
        </div>
        <div className="rounded-xl border border-border bg-background p-3">
          <p className="text-xs text-muted-foreground">Weight</p>
          <p className="font-mono">{details.weight.toString()}</p>
        </div>
        <div className="rounded-xl border border-border bg-background p-3">
          <p className="text-xs text-muted-foreground">Rarity Weight</p>
          <p className="font-mono">{details.rarityWeight.toString()}</p>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-border bg-background p-4">
        <p className="font-pixel text-xs text-primary">VAULT BALANCE</p>
        <div className="mt-2 space-y-1">
          {ASSETS.map((asset, idx) => (
            <div key={asset.symbol} className="flex justify-between text-sm">
              <span className="font-mono">{asset.symbol}</span>
              <span className="text-muted-foreground">{formatUnits(balances[idx] ?? 0n, 18)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
